import React, { FC, useState } from 'react';
import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';

import PixIcon from '@mui/icons-material/SwapHoriz';
import BarcodeIcon from '@mui/icons-material/QrCodeScanner';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { IAccount } from 'common/accounts.interface';
import { EFeatureAvailability } from 'common/carrier.interface';
import PixTransferModal from '../modals/PixTransferModal';
import BankSlipModal from '../modals/BankSlipModal';
import PamcardModal from '../modals/PamcardModal';

type PaymentOption = {
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export const PaymentsOptions: FC<{ onReload?: () => void; account?: IAccount; pamcardMenuState?: EFeatureAvailability }> = ({
  onReload,
  account,
  pamcardMenuState,
}) => {
  const theme = useTheme();

  const [openPixModal, setOpenPixModal] = useState(false);
  const [openBoletoModal, setOpenBoletoModal] = useState(false);
  const [openPamcardModal, setOpenPamcardModal] = useState(false);

  const options: PaymentOption[] = [
    {
      label: 'Transferência PIX',
      icon: <PixIcon fontSize="medium" />,
      onClick: () => setOpenPixModal(true),
    },
    {
      label: 'Boleto e Tributos',
      icon: <BarcodeIcon fontSize="medium" />,
      onClick: () => setOpenBoletoModal(true),
    },
    {
      label: 'Salário e Benefícios',
      icon: <PaymentsIcon fontSize="medium" />,
      disabled: true,
    },
    ...(pamcardMenuState !== EFeatureAvailability.HIDDEN
      ? [
          {
            label: 'Carregar meu Pamcard',
            icon: <CreditCardIcon fontSize="medium" />,
            disabled: pamcardMenuState !== EFeatureAvailability.ENABLED,
            onClick: () => setOpenPamcardModal(true),
          },
        ]
      : []),
  ];

  return (
    <>
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          O que você gostaria de pagar?
        </Typography>

        <Grid container spacing={2}>
          {options.map((option) => (
            <Grid item xs={12} sm={6} md={3} key={option.label}>
              <Paper
                elevation={0}
                onClick={!option.disabled ? option.onClick : undefined}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  gap: 1,
                  p: 2,
                  height: 96,
                  borderRadius: 2,
                  cursor: option.disabled ? 'default' : 'pointer',
                  backgroundColor: option.disabled ? theme.palette.action.disabledBackground : theme.palette.grey[100],
                  color: option.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                  transition: 'background-color 0.2s ease',
                  '&:hover': option.disabled
                    ? {}
                    : {
                        backgroundColor: theme.palette.grey[200],
                      },
                }}
              >
                <Box
                  sx={{
                    color: option.disabled ? theme.palette.text.disabled : theme.palette.primary.main,
                  }}
                >
                  {option.icon}
                </Box>

                <Typography variant="body2" fontWeight={500}>
                  {option.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <PixTransferModal
        open={openPixModal}
        onClose={() => setOpenPixModal(false)}
        onSuccess={() => {
          onReload?.();
        }}
        account={account}
      />
      <BankSlipModal open={openBoletoModal} onClose={() => setOpenBoletoModal(false)} />
      <PamcardModal
        open={openPamcardModal}
        onClose={() => setOpenPamcardModal(false)}
        onSuccess={() => {
          onReload?.();
        }}
      />
    </>
  );
};
